import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { showToast } from "../ShowToast/ToastNotification";
import { api_url } from "../../constant/constant";
import { t } from "i18next";

function PhishingCompaignSetting({ id }) {
    const [campaignData, setCampaignData] = useState(null);
    const [emailServers, setEmailServers] = useState([]);
    const [servers, setServers] = useState([]);
    const [templatesEn, setTemplatesEn] = useState([]);
    const [templatesAr, setTemplatesAr] = useState([]);
    const [selectedEnglishTemplate, setSelectedEnglishTemplate] = useState("");
    const [selectedArabicTemplate, setSelectedArabicTemplate] = useState("");
    const [selectedServer, setSelectedServer] = useState("");
    const [fromEmail, setFromEmail] = useState("");
    const [fromName, setFromName] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [fakeDomain, setFakeDomain] = useState("");
    const [isAutoSend, setIsAutoSend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ zip_file: null });

    useEffect(() => {
        fetchCampaignSettingData();
        fetchEmailServers();
        fetchEmailTemplateEn();
        fetchEmailTemplateAr();
    }, []);

    const handleChange = (e) => {
        const { name, type, value, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' && files.length > 0 ? files[0] : value,
        }));
    };

    const fetchCampaignSettingData = async () => {
        try {
            const token = localStorage.getItem("userToken");
            const response = await fetch(`${api_url}/phishing-campaign-settings/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
    
            if (!response.ok && response.status !== 404) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
    
            const result = await response.json();
    
            if (result?.data) {
                const campaign = result.data;
                setCampaignData(campaign);
                setSelectedEnglishTemplate(campaign.email_template_en_id || "");
                setSelectedArabicTemplate(campaign.email_template_ar_id || "");
                setSelectedServer(campaign.server_id || "");
                setFromEmail(campaign.from_email_address || "");
                setFromName(campaign.from_name || "");
                setEmailSubject(campaign.email_subject || "");
                setFakeDomain(campaign.fake_domain || "");
                setIsAutoSend(campaign.is_auto_send === 1);
            }
        } catch (error) {
            console.error("Failed to fetch campaign data:", error);
            showToast(t("errorFetchingCampaignData"), "error");
        }
    };
    

    const fetchEmailTemplateEn = async () => {
        try {
            const token = localStorage.getItem("userToken");
            const response = await fetch(`${api_url}/phishing-campaign-settings/email-templates/en`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const result = await response.json();
            const templates = result?.data || [];

            if (templates.length > 0) {
                setTemplatesEn(templates);
            } else {
                showToast(t("noEmailTemplatesFound"), "error");
            }
        } catch (error) {
            console.error("Error fetching English templates:", error);
            showToast(t("failedToFetchTemplates"), "error");
        }
    };

    const fetchEmailTemplateAr = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch(`${api_url}/phishing-campaign-settings/email-templates/ar`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const result = await response.json();
            const templates = result?.data || [];

            if (templates.length > 0) {
                setTemplatesAr(templates);
            } else {
                showToast(t('noEmailTemplatesFound'), 'error');
            }
        } catch (error) {
            console.error('Error fetching Arabic templates:', error);
            showToast(t('failedToFetchTemplates'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmailServers = async () => {
        try {
            const token = localStorage.getItem("userToken");
            const response = await fetch(`${api_url}/email-servers`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const result = await response.json();
            setServers(result.data.data|| []);
        } catch (error) {
            console.error("Failed to fetch email servers:", error);
            showToast(t("errorFetchingEmailServers"), "error");
            setServers([]);
        }
    };

    const handleSaveEmail = async (e) => {
        e.preventDefault();

        if (!selectedEnglishTemplate || !selectedArabicTemplate || !selectedServer || !fromEmail || !fromName || !emailSubject || !fakeDomain || !formData.zip_file) {
            showToast("Please fill in all fields.", "error");
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("userToken");
            const formPayload = new FormData();
            formPayload.append("campaign_id", id);
            formPayload.append("email_template_en_id", selectedEnglishTemplate);
            formPayload.append("email_template_ar_id", selectedArabicTemplate);
            formPayload.append("server_id", selectedServer);
            formPayload.append("from_email_address", fromEmail);
            formPayload.append("from_name", fromName);
            formPayload.append("fake_domain", fakeDomain);
            formPayload.append("email_subject", emailSubject);
            formPayload.append("is_auto_send", isAutoSend ? 1 : 0);
            formPayload.append("zip_file", formData.zip_file);

            const response = await fetch(`${api_url}/phishing-campaign-settings`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formPayload,
            });

            const data = await response.json();
            if (response.ok && data?.status === "success") {
                showToast(t("phishingcampaignsettingSaved"), "success");
            } else {
                if (data?.data?.zip_file && Array.isArray(data.data.zip_file)) {
                    // Show the first zip_file error message
                    showToast(data.data.zip_file[0], "error");
                } else if (data?.message) {
                    showToast(data.message, "error");
                } else {
                    showToast(t("errorSavingphishingcampaignsetting"), "error");
                }
            }
        } catch (err) {
            console.error("Error saving email settings:", err);
            showToast(t("errSavingphishingcampaignsetting"), "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid" style={{ border: '1px solid #EAEAEA', borderRadius: '10px', padding: '15px' }}>
            <form onSubmit={handleSaveEmail}>
                <div className="row g-3 mt-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('EnglishTemplate')}</label>
                        <select className="form-select" value={selectedEnglishTemplate} onChange={(e) => setSelectedEnglishTemplate(e.target.value)} required>
                            <option value="">{t('SelectEnglishTemplate')}</option>
                            {templatesEn.map((template) => (
                                <option key={template.id} value={template.id}>{template.title}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('ArabicTemplate')}</label>
                        <select className="form-select" value={selectedArabicTemplate} onChange={(e) => setSelectedArabicTemplate(e.target.value)} required>
                            <option value="">{t('SelectArabicTemplate')}</option>
                            {templatesAr.map((template) => (
                                <option key={template.id} value={template.id}>{template.title}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="row g-3 mt-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('EmailServer')}</label>
                        <select className="form-select" value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} required>
                            <option value="">{t('SelectEmailServer')}</option>
                            {servers.length > 0 ? (
                                servers.map((server) => (
                                    <option key={server.id} value={server.id}>{server.name}</option>
                                ))
                            ) : (
                                <option disabled>{t('Noserversfound')}</option>
                            )}
                        </select>
                    </div>
                    <div className="col-md-6 mb-3">
                    <label className="form-label">{t('ZipFile')}</label>
                    <input
                        type="file"
                        name="zip_file"
                        className="form-control"
                        accept=".zip"
                        onChange={handleChange}
                        required
                    />
                </div>
                </div>

                <div className="row g-3 mt-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('FromEmailAddress')}</label>
                        <input type="email" className="form-control" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} required />
                    </div>
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('FromName')}</label>
                        <input type="text" className="form-control" value={fromName} onChange={(e) => setFromName(e.target.value)} required />
                    </div>
                </div>

                <div className="row g-3 mt-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('FakeDomain')}</label>
                        <input type="text" className="form-control" value={fakeDomain} onChange={(e) => setFakeDomain(e.target.value)} required />
                    </div>
                    <div className="col-12 col-md-6">
                        <label className="form-label">{t('EmailSubject')}</label>
                        <input type="text" className="form-control" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} required />
                    </div>
                </div>

           
                <div className="col-12 col-md-4 d-flex align-items-center" style={{ marginTop: '40px' }}>
                        <div className="d-flex align-items-center">
                            <input type="checkbox" className="form-check-input me-2" checked={isAutoSend} onChange={() => setIsAutoSend(!isAutoSend)} />
                            <label className="form-label mt-2">{t('AutoSendEmail')}</label>
                        </div>
                    </div>
                <div className="d-flex justify-content-end mt-3">
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t("Saving...") : t("Save")}
                    </Button>
                </div>
            </form>
        </div>
    );
}

export default PhishingCompaignSetting;
